import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiX, FiPlus, FiEdit2, FiTrash2, FiTag } from 'react-icons/fi';
import { useConfirm } from '../ui/ConfirmDialog';

const TagManagerModal = ({ onClose, onTagsUpdated }) => {
  const confirm = useConfirm();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#3b82f6'
  });

  const colorOptions = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
    '#8b5cf6', '#06b6d4', '#f97316', '#ec4899',
    '#84cc16', '#6366f1', '#14b8a6', '#f43f5e'
  ];

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/planwise/tags');
      setTags(response.data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Tag name is required');
      return;
    }

    try {
      if (editingTag) {
        const response = await axios.put(`/api/planwise/tags/${editingTag.id}`, formData);
        setTags(tags.map(tag => tag.id === editingTag.id ? response.data : tag));
        toast.success('Tag updated successfully');
      } else {
        const response = await axios.post('/api/planwise/tags', formData);
        setTags([...tags, response.data]);
        toast.success('Tag created successfully');
      }

      setFormData({ name: '', color: '#3b82f6' });
      setShowCreateForm(false);
      setEditingTag(null);

      if (onTagsUpdated) onTagsUpdated();
    } catch (error) {
      console.error('Error saving tag:', error);

      let errorMessage = editingTag ? 'Failed to update tag' : 'Failed to create tag';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to manage tags. Please contact your team admin.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and try again.';
            break;
          case 400:
            errorMessage = `Invalid tag data: ${error.response.data?.message || 'Please check your input and try again'}`;
            break;
          case 409:
            errorMessage = 'A tag with this name already exists. Please choose a different name.';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later.';
            break;
          default:
            errorMessage = `${editingTag ? 'Failed to update tag' : 'Failed to create tag'}: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      toast.error(errorMessage);
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({ name: tag.name, color: tag.color });
    setShowCreateForm(true);
  };

  const handleDelete = async (tagId) => {
    const ok = await confirm({
      title: 'Delete this tag?',
      description: 'It will be removed from all tasks.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      await axios.delete(`/api/planwise/tags/${tagId}`);
      setTags(tags.filter(tag => tag.id !== tagId));
      toast.success('Tag deleted successfully');

      if (onTagsUpdated) onTagsUpdated();
    } catch (error) {
      console.error('Error deleting tag:', error);

      let errorMessage = 'Failed to delete tag';

      if (error.response) {
        switch (error.response.status) {
          case 404:
            errorMessage = 'Tag not found. It may have already been deleted.';
            break;
          case 403:
            errorMessage = 'You do not have permission to delete this tag. Please contact your team admin.';
            break;
          case 401:
            errorMessage = 'Your session has expired. Please refresh the page and try again.';
            break;
          case 409:
            errorMessage = 'Cannot delete tag as it is being used by tasks. Please remove it from all tasks first.';
            break;
          case 500:
            errorMessage = 'Server error occurred while deleting the tag. Please try again later.';
            break;
          default:
            errorMessage = `Failed to delete tag: ${error.response.data?.message || 'Unknown error occurred'}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', color: '#3b82f6' });
    setShowCreateForm(false);
    setEditingTag(null);
  };

  return createPortal(
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-xl w-full shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FiTag className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Tags Manager
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Create / Edit form */}
          {showCreateForm && (
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-md border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                {editingTag ? 'Edit Tag' : 'Create New Tag'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white"
                    placeholder="Enter tag name"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Colour
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-7 h-7 rounded-md border-2 transition-transform ${
                          formData.color === color
                            ? 'border-gray-900 dark:border-white'
                            : 'border-transparent hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                        style={{ backgroundColor: color }}
                        aria-label={`Colour ${color}`}
                      />
                    ))}
                  </div>

                  {/* Preview */}
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Preview:</span>
                    <span
                      className="inline-block px-2.5 py-0.5 text-xs font-medium rounded-full border"
                      style={{
                        backgroundColor: `${formData.color}20`,
                        borderColor: `${formData.color}40`,
                        color: formData.color
                      }}
                    >
                      {formData.name || 'Tag Preview'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
                  >
                    {editingTag ? 'Update Tag' : 'Create Tag'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Add tag button */}
          {!showCreateForm && (
            <div className="mb-4">
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                <FiPlus className="w-4 h-4" />
                Add New Tag
              </button>
            </div>
          )}

          {/* Tags list */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Existing Tags
              </h3>
              <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300">
                {tags.length}
              </span>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mb-2"></div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Loading tags…</p>
              </div>
            ) : tags.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <FiTag className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                  No tags created yet
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Create your first tag to organise your tasks
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {tags.map((tag) => (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between p-2.5 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    <span
                      className="px-2 py-0.5 text-xs font-medium rounded-full border"
                      style={{
                        backgroundColor: `${tag.color}20`,
                        borderColor: `${tag.color}40`,
                        color: tag.color
                      }}
                    >
                      {tag.name}
                    </span>

                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => handleEdit(tag)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                        title="Edit tag"
                      >
                        <FiEdit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(tag.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete tag"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TagManagerModal;
